import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import { WelcomeTemplateEntity } from '../../../../../types';
import SharedReviewLayout, {
  ReviewSection,
} from '../../../../shared/shared-review-layout';
import { UserFormValues } from '../user-schema';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';

interface StepSignatureTemplateReviewProps {
  selectedUser: UserFormValues;
  templates: WelcomeTemplateEntity[];
}

function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export default function StepReview({
  selectedUser,
}: StepSignatureTemplateReviewProps) {
  const { features } = useUserSettings();
  const isIntegrationsEnabled = isFeatureEnabled('slackIntegration', features);

  const queryClient = useQueryClient();
  const slackChannelsData = queryClient.getQueryData<{
    pages?: { channels?: { id: string; name: string }[] }[];
  }>([UserKeys.SlackChannels]);
  const allSlackChannels =
    slackChannelsData?.pages?.flatMap((page) => page.channels || []) || [];
  const userEmail = `${selectedUser.userName}@${selectedUser.domain}`;

  const reviewSection: ReviewSection[] = [
    // Setup User Section
    {
      title: 'Setup User',
      leftWidth: {
        labelWidth: 7,
        valueWidth: 5,
      },
      rightWidth: {
        labelWidth: 7,
        valueWidth: 5,
      },
      leftItems: [
        {
          label: 'User Email',
          value: [{ email: userEmail, type: 'User' }],
          variant: 'chip',
        },
        { label: 'First Name', value: selectedUser?.name?.givenName },
        { label: 'Last Name', value: selectedUser?.name?.familyName },
        {
          label: 'Require user to change password at next sign-in',
          value: selectedUser.changePasswordAtNextLogin,
        },
      ],
      rightItems: [
        {
          label: 'Organization Unit',
          value: [
            {
              email: selectedUser.orgUnitPath || '',
              type: 'OrgUnit' as const,
            },
          ],
          variant: 'chip',
        },
        {
          label: 'cPanel Role',
          value: selectedUser.cPanelRole
            ? capitalize(selectedUser.cPanelRole)
            : '-',
        },
        {
          label: 'gPanel Role',
          value: selectedUser.gPanelRole ? selectedUser.gPanelRole : '-',
        },
        {
          label: `Automatically share ${
            selectedUser?.name?.givenName &&
            selectedUser?.name?.givenName.length > 0
              ? selectedUser.name.givenName
              : 'user'
          }'s contact information.`,
          value: selectedUser.includeInGlobalAddressList,
        },
      ],
    },

    // Groups Section
    {
      title: 'Groups',
      leftItems: [
        {
          value:
            selectedUser.groups && selectedUser.groups.length > 0
              ? selectedUser.groups.map((target) => ({
                  email: target.groupId,
                  type: 'Group' as const,
                }))
              : '',
          variant: 'chip',
          isVisible: !!(selectedUser.groups && selectedUser.groups.length > 0),
        },
      ],
    },

    // Policies Section
    {
      title: 'Policies',
      leftItems: [
        {
          value:
            selectedUser.policies && selectedUser.policies.length > 0
              ? selectedUser.policies.map((value) => ({
                  email: `${value.policyName}`,
                  type: 'Unknown' as const,
                }))
              : '',
          variant: 'chip',
          isVisible: !!(
            selectedUser.policies && selectedUser.policies.length > 0
          ),
        },
      ],
    },

    // Integrations Section
    {
      title: 'Integrations',
      leftItems: [
        {
          label: 'Slack Channels',
          value:
            selectedUser.slackChannels && selectedUser.slackChannels.length > 0
              ? selectedUser.slackChannels.map((channelId) => {
                  const channel = allSlackChannels.find(
                    (c) => c.id === channelId
                  );
                  return {
                    email: channel ? channel.name : channelId,
                    type: 'Unknown' as const,
                  };
                })
              : '',
          variant: 'chip',
          isVisible:
            isIntegrationsEnabled &&
            !!(
              selectedUser.slackChannels &&
              selectedUser.slackChannels.length > 0
            ),
        },
      ],
    },
  ];

  return <SharedReviewLayout sections={reviewSection} />;
}
